/**
 * Auth API client - cookie-based JWT (Phase 4 SEC-06 / D-05 / D-08).
 *
 * The server sets leviathan_auth + leviathan_refresh + leviathan_csrf
 * cookies on login/register/refresh. This module NEVER touches localStorage
 * and NEVER reads the JWT itself - the JWT cookie is httpOnly and the
 * refresh cookie is httpOnly + Path-scoped to /api/auth/refresh.
 *
 * The only cookie we read from JS is `leviathan_csrf`, which is intentionally
 * non-httpOnly so we can echo it back as the `X-CSRF-Token` header on every
 * state-changing request (double-submit pattern enforced by api/core/csrf.py).
 */

import axios from 'axios'

const _apiOrigin: string = import.meta.env.VITE_API_URL ?? ''
const authClient = axios.create({
  baseURL: `${_apiOrigin}/api/auth`,
  withCredentials: true,
})

// ---------------------------------------------------------------------------
// CSRF - same idiom as api.ts (intentional duplication; two clients are
// small enough not to justify extracting a shared module yet).
// ---------------------------------------------------------------------------

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

authClient.interceptors.request.use((config) => {
  const csrf = readCookie('leviathan_csrf')
  if (csrf) {
    config.headers = config.headers ?? {}
    config.headers['X-CSRF-Token'] = csrf
  }
  return config
})

// ---------------------------------------------------------------------------
// Response / request types (post-Phase-4 cookie shapes - no token fields)
// ---------------------------------------------------------------------------

export interface UserProfile {
  id: string
  email: string
  name: string | null
  role: 'admin' | 'analyst' | 'viewer'
  org_id: string
  org_name: string | null
  created_at: string | null
  last_login_at: string | null
  email_verified: boolean
}

export interface LoginResponse {
  user: UserProfile
}

export interface RegisterData {
  email: string
  password: string
  name: string
  role?: 'admin' | 'analyst' | 'viewer'
  org_name?: string
}

export interface RegisterResponse {
  user_id: string
  user: UserProfile
}

export interface RefreshResponse {
  user: UserProfile
}

export interface ApiKey {
  id: string
  name: string
  key_prefix: string
  created_at: string | null
  last_used_at: string | null
  revoked: boolean
}

export interface ApiKeyCreated extends ApiKey {
  key: string // full key - shown once only
}

// ---------------------------------------------------------------------------
// AuthRefreshFailedError - exported class so callers can `instanceof` it
// to distinguish session expiry from a real API failure.
// ---------------------------------------------------------------------------

export class AuthRefreshFailedError extends Error {
  constructor(message = 'Session refresh failed') {
    super(message)
    this.name = 'AuthRefreshFailedError'
    // Restore prototype chain (TypeScript / ES5 quirk).
    Object.setPrototypeOf(this, AuthRefreshFailedError.prototype)
  }
}

// ---------------------------------------------------------------------------
// Helper - extract a human-readable error message from an axios error
// ---------------------------------------------------------------------------

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) return detail.map((d) => d.msg ?? d).join(', ')
    return err.message
  }
  return err instanceof Error ? err.message : 'Unknown error'
}

// ---------------------------------------------------------------------------
// Auth API object
// ---------------------------------------------------------------------------

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const { data } = await authClient.post<LoginResponse>('/login', { email, password })
      return data
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async register(data: RegisterData): Promise<RegisterResponse> {
    try {
      const { data: resp } = await authClient.post<RegisterResponse>('/register', data)
      return resp
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async refresh(): Promise<RefreshResponse> {
    // No body - server reads leviathan_refresh cookie. Cookies are rotated
    // via Set-Cookie on the response, so the next request automatically
    // picks them up. We do not store anything here.
    const { data } = await authClient.post<RefreshResponse>('/refresh')
    return data
  },

  async logout(): Promise<void> {
    try {
      await authClient.post('/logout')
    } catch {
      // Best-effort - server clears cookies via Set-Cookie max-age=0
      // regardless of any client-side state.
    }
  },

  async me(): Promise<UserProfile> {
    try {
      const { data } = await authClient.get<UserProfile>('/me')
      return data
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async updateMe(
    payload: { name?: string; password?: string; current_password?: string },
  ): Promise<UserProfile> {
    try {
      const { data } = await authClient.patch<UserProfile>('/me', payload)
      return data
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async generateApiKey(name: string): Promise<ApiKeyCreated> {
    try {
      const { data } = await authClient.post<ApiKeyCreated>('/api-keys', { name })
      return data
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async listApiKeys(): Promise<ApiKey[]> {
    try {
      const { data } = await authClient.get<{ keys: ApiKey[] }>('/api-keys')
      return data.keys
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async revokeApiKey(id: string): Promise<void> {
    try {
      await authClient.delete(`/api-keys/${id}`)
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async forgotPassword(email: string): Promise<string> {
    try {
      const { data } = await authClient.post<{ message: string }>('/forgot-password', { email })
      return data.message
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async resetPassword(token: string, password: string): Promise<string> {
    try {
      const { data } = await authClient.post<{ message: string }>('/reset-password', { token, password })
      return data.message
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async verifyEmail(token: string): Promise<string> {
    try {
      const { data } = await authClient.post<{ message: string }>('/verify-email', { token })
      return data.message
    } catch (err) {
      throw new Error(extractError(err))
    }
  },

  async resendVerification(): Promise<string> {
    try {
      const { data } = await authClient.post<{ message: string }>('/resend-verification')
      return data.message
    } catch (err) {
      throw new Error(extractError(err))
    }
  },
}
